import {
  useClearUserGroupCache,
  useListUserGroups,
} from "#src/components/hooks/adapters/useUserGroups";
import { useMutation } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  DropdownInput,
  Form,
  useForm,
  useToast,
} from "@validereinc/common-components";
import {
  DomainAdapterError,
  UserGroupsAdapter,
  UserReturnType,
} from "@validereinc/domain";
import pLimit from "p-limit";
import React, { useEffect } from "react";

export const AddUserGroupMembershipDialog = ({
  isOpen,
  onClose,
  user,
}: {
  isOpen: boolean;
  onClose: () => void;
  user: UserReturnType;
}) => {
  const form = useForm({});
  const { toast } = useToast();
  const { invalidateUserGroup } = useClearUserGroupCache();
  const addMemberToUserGroups = useMutation({
    mutationFn: ({
      user,
      groupIds,
    }: {
      user: UserReturnType;
      groupIds: string[];
    }) => {
      const limitPromises = pLimit(8);

      return Promise.all(
        groupIds.map((groupId) =>
          limitPromises(() =>
            UserGroupsAdapter.members.add({
              id: user.id,
              data: null,
              meta: { userGroupId: groupId },
            })
          )
        )
      );
    },
    onSuccess: (_, variables) => {
      invalidateUserGroup();
      onClose?.();
      toast.push({
        intent: "success",
        description: `Successfully added ${variables.user.name} to ${variables.groupIds.length} user group(s)`,
      });
    },
    onError: (err, vars) => {
      const normalizedErr = new DomainAdapterError(
        `Failed to add user ${vars.user.id} to user group(s)`,
        {
          cause: err,
          getExplanation: () => "Failed to add user to user group(s)",
        }
      );
      toast.push({
        intent: "error",
        description: normalizedErr.getExplanation(),
      });
    },
  });
  const existingUserGroupMemberships = useListUserGroups(
    {
      filters: {
        "user.id": user.id,
      },
    },
    {
      enabled: !!user.id,
    }
  );
  const userGroups =
    useListUserGroups({
      // REVIEW: BE bug preventing $ne filtering from working
    }).data?.data ?? [];

  const handleSubmit = form.handleSubmit((values) => {
    const groupIds = values.user_group;

    addMemberToUserGroups.mutate({ user, groupIds });
  });

  useEffect(() => {
    form.reset();
  }, [isOpen]);

  return (
    <Dialog
      title="Add User to User Group"
      isOpen={isOpen}
      onClose={onClose}
      actionRow={[
        <Button
          key="add-user-group-membership"
          variant="primary"
          onClick={handleSubmit}
          isLoading={addMemberToUserGroups.isLoading}
        >
          Add
        </Button>,
      ]}
    >
      <Form {...form}>
        <DropdownInput
          label="User Groups"
          name="user_group"
          placeholder="Select user group(s)"
          description="Select one or more user groups to add this user to. Groups that already have this user are excluded."
          options={userGroups
            // REVIEW: manually filtering till BE bug is fixed
            .filter(
              (ug) =>
                !existingUserGroupMemberships.data?.data?.find(
                  (ugm) => ugm.id === ug.id
                )
            )}
          labelKey="name"
          valueKey="id"
          isFluid
          isMulti
          isSearchable
          isRequired
          isSortedAlphabetically
        />
      </Form>
    </Dialog>
  );
};
